import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";

/** Re-render cadence for the relative label. A minute is the smallest unit it shows. */
const TICK_MS = 30_000;

/** Turns an age in milliseconds into the coarsest phrase that is still true.
 *  Counts go through i18next plurals — Arabic has six forms, not two. */
function describeAge(ageMs: number, t: TFunction): string {
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return t("common:dashboard_grid.updated_just_now");
  if (minutes < 60)
    return t("common:dashboard_grid.updated_minutes_ago", { count: minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return t("common:dashboard_grid.updated_hours_ago", { count: hours });
  return t("common:dashboard_grid.updated_over_a_day_ago");
}

/**
 * How old the numbers on screen are, and how to make them new.
 *
 * A dashboard people leave open all day raises a question it could not answer:
 * is this still true? RTK Query refetches on its own schedule and says nothing
 * about it, so a canvas of stale figures is indistinguishable from a fresh one.
 *
 * The label is deliberately coarse and only re-renders twice a minute — a
 * seconds-accurate counter ticking next to the data would pull the eye away from
 * the data, which is the opposite of the point.
 */
export function useFreshness(onRefresh?: () => void | Promise<void>) {
  const { t } = useTranslation(["common"]);
  /**
   * Null until the user actually refreshes.
   *
   * Stamping mount time here would have the label say "just now" over figures
   * RTK Query served from a cache filled ten minutes ago — a freshness
   * indicator that lies is worse than none, because it stops people checking.
   * Until there is something true to report it reports nothing.
   */
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  /**
   * `null` until there is a real age to report — not the string "Refresh".
   *
   * That default made one value mean two things: a freshness reading, or the
   * name of a button. The command bar consumed it as button copy, which worked
   * only for as long as the bar had one control doing both jobs. It now reports
   * freshness as text and refreshes with a button beside it, and neither can be
   * written until the value says which of the two it is.
   */
  const [label, setLabel] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (refreshedAt === null) {
      setLabel(null);
      return;
    }
    const update = () => setLabel(describeAge(Date.now() - refreshedAt, t));
    update();
    const timer = setInterval(update, TICK_MS);
    return () => clearInterval(timer);
  }, [refreshedAt, t]);

  const refresh = useCallback(async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
      // Stamped after the invalidation lands, not before, so the label cannot
      // claim "just now" for a refresh that failed.
      if (isMountedRef.current) setRefreshedAt(Date.now());
    } finally {
      if (isMountedRef.current) setIsRefreshing(false);
    }
  }, [isRefreshing, onRefresh]);

  return { label, isRefreshing, refresh, canRefresh: Boolean(onRefresh) };
}
